import { useMemo, useState } from 'react';
import type { CeoDebtScheduleItem } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMoney } from '../ceoFormat';
import {
  ActionButtonsRow,
  CreditorContractText,
  DueSubtext,
  HeaderRow,
  InstrumentTitleText,
  RatesSubtext,
  Section,
  Table,
  TableWrap,
  Title,
} from './styles';

/** Горизонт списка «К оплате», дней. */
const DUE_HORIZON_DAYS = 90;

interface UpcomingScheduleTableProps {
  schedule: CeoDebtScheduleItem[];
  can_write: boolean;
  onSelectInstrument: (id: string) => void;
  onMarkPaid: (itemId: string, amount: number) => void;
}

export function UpcomingScheduleTable({
  schedule,
  can_write,
  onSelectInstrument,
  onMarkPaid,
}: UpcomingScheduleTableProps) {
  const [showAll, setShowAll] = useState(false);
  // По умолчанию — неоплаченные: просроченные и на ближайшие 90 дней.
  const due = useMemo(
    () => schedule.filter((i) => i.status !== 'paid' && i.days_until_due <= DUE_HORIZON_DAYS),
    [schedule],
  );
  const rows = showAll ? schedule : due;
  return (
    <Section>
      <HeaderRow>
        <Title>
          {showAll
            ? 'Пошаговый платёжный календарь по кредитам и лизингам'
            : `К оплате: просроченные и на ${DUE_HORIZON_DAYS} дней`}
        </Title>
        {schedule.length > due.length && (
          <Button $size="sm" $variant="ghost" type="button" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Только к оплате' : `Весь график (${schedule.length})`}
          </Button>
        )}
      </HeaderRow>
      {rows.length === 0 && (
        <RatesSubtext>
          {showAll
            ? 'Графиков платежей нет.'
            : `Платежей к оплате на ${DUE_HORIZON_DAYS} дней нет.`}
        </RatesSubtext>
      )}
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Дата платежа</th>
              <th>Договор / Кредитор</th>
              <th>Транш</th>
              <th>Основной долг</th>
              <th>Проценты / Лизинг</th>
              <th>Комиссия</th>
              <th>Итого к оплате</th>
              <th>Статус</th>
              <th>Действие</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const isPaid = item.status === 'paid';
              return (
                <tr key={item.id}>
                  <td>
                    <strong>{formatDateRu(item.due_date)}</strong>
                    {!isPaid && (
                      <DueSubtext>
                        {item.days_until_due < 0
                          ? `просрочено ${Math.abs(item.days_until_due)} дн.`
                          : `через ${item.days_until_due} дн.`}
                      </DueSubtext>
                    )}
                  </td>
                  <td>
                    <CreditorContractText>
                      {item.creditor_name} · № {item.contract_number}
                    </CreditorContractText>
                    <InstrumentTitleText>{item.instrument_title}</InstrumentTitleText>
                  </td>
                  <td>#{item.installment_no}</td>
                  <td>{formatMoney(item.principal_amount)}</td>
                  <td>{formatMoney(item.interest_amount)}</td>
                  <td>{item.fee_amount > 0 ? formatMoney(item.fee_amount) : '—'}</td>
                  <td>
                    <strong>{formatMoney(item.total_payment)}</strong>
                  </td>
                  <td>
                    {isPaid ? (
                      <Badge $tone="success">Оплачено</Badge>
                    ) : item.status === 'overdue' ? (
                      <Badge $tone="danger">Просрочено</Badge>
                    ) : (
                      <Badge $tone="neutral">Плановый</Badge>
                    )}
                  </td>
                  <td>
                    <ActionButtonsRow>
                      <Button
                        $size="sm"
                        $variant="ghost"
                        onClick={() => onSelectInstrument(item.instrument_id)}
                      >
                        Карточка
                      </Button>
                      {can_write && !isPaid && (
                        <Button
                          $size="sm"
                          $variant="secondary"
                          onClick={() => onMarkPaid(item.id, item.total_payment)}
                        >
                          Оплачено
                        </Button>
                      )}
                    </ActionButtonsRow>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
